import React, { useState, useEffect, useMemo, SetStateAction } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import { RiSearchLine } from "react-icons/ri";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import TableContainer from "Components/Base/BaseTable";
import DeleteModal from "Components/Base/DeleteModal";
import BaseModal from "Components/Base/BaseModal";
import {
  createService,
  deleteService,
  updateService,
  listOfService,
  viewService,
  calculateServiceGstApi,
} from "Api/service";
import {
  checkStatusCodeSuccess,
  Loading,
  notFound,
  rupeeSymbol,
  Search,
  TaxType,
} from "Components/constants/Common";
import {
  addServiceLabel,
  editServiceLabel,
  ServiceName,
  serviceLabel,
  servicesLabel,
  Cost,
  servicesKey,
  costKey,
  Description,
  descriptonKey,
  hsnSacKey,
  gstKey,
  amountKey,
  GstAmount,
  gstAmountKey,
  WithGstAmount,
} from "Components/constants/Services";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import {
  ActionKey,
  ActionLabel,
  DeleteLabel,
  editLabel,
  NewLabel,
  cancelLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import { hsnDropdownApi } from "Api/Quotation";
import { BaseSelect } from "Components/Base/BaseSelect";
import { HSNSACLabel } from "Components/constants/HsnSac";
import { GST_LABEL } from "Components/constants/ROModule";
import { QuotationLabel } from "Components/constants/Quotation";

interface ServicePayload {
  service_name: string;
  description: string;
  cost: number | null;
  hsn_sac_id: number | null;
  gst: string | null;
  with_gst_amount: number | null;
  gst_amount: number | null;
  serviceId?: number | any;
}

const Services = () => {
  document.title = servicesLabel;

  const [loader, setLoader] = useState<boolean>(false);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [services, setServices] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(
    null
  );
  const [hsnSacOption, setHsnSacOption] = useState<Array<any>>([]);
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [modalLoader, setModalLoader] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [serviceSearchData, setServiceSearchData] = useState<Array<any>>([]);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [columnName, setColumnName] = useState<string>("id");
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    formik.resetForm();
    setSelectedServiceId(null);
    setEditMode(false);
  };

  const fetchHsnSac = (callback?: (data: any[]) => void) => {
      setLoader(true);
      hsnDropdownApi()
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const data = res?.data?.map((item: any) => ({
              value: item?.id,
              label: item?.code,
              type: item?.type,
              id: item?.id,
            }));
            setHsnSacOption(data);
            if (callback) {
              callback(res.data);
            }
          } else {
            setHsnSacOption([]);
          }
        })
        .catch((err) => {
          setHsnSacOption([]);
        })
        .finally(() => {
          setLoader(false);
        });
  };
  
  useEffect(() => {
    fetchHsnSac();
  }, []);

  const fetchServices = async () => {
    const payload = {
      pageSize,
      page: currentPage,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfService(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setServices(res?.data?.services || []);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          toast.success(res?.message);
        } else {
          setServices([]);
        }
      })
      .catch(() => {
        setServices([]);
      })
      .finally(() => setLoader(false));
  };

  const fetchSearchServiceData = () => {
    const payload = {
      pageSize,
      page: currentPage,
      search: searchValue,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfService(payload)
      .then((response) => {
        if (checkStatusCodeSuccess(response.statusCode)) {
          setServiceSearchData(response?.data?.services || []);
          setTotalRecords(response?.data?.totalItems || 0);
          setTotalPages(response?.data?.totalPages);
          setCurrentPage(response?.data?.currentPage);
          setTotalNumberOfRows(response?.data?.numberOfRows);
          toast.success(response?.message);
        } else {
          setServiceSearchData([]);
        }
      })
      .catch((error) => {
        setServiceSearchData([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    if (searchValue) {
      fetchSearchServiceData();
    } else {
      fetchServices();
    }
  }, [currentPage, pageSize, searchValue, sortOrder, columnName]);

  const formik = useFormik({
    initialValues: {
      service_name: "",
      description: "",
      hsnSac: null,
      gst: null,
      cost: null,
      gstAmount: null,
      withGstAmount: null,
      serviceId: null,
    },
    validationSchema: Yup.object({
      service_name: Yup.string().required(
        validationMessages.required(ServiceName)
      ),
      description: Yup.string().required(
        validationMessages.required(Description)
      ),
      hsnSac: Yup.string().required(validationMessages.required(HSNSACLabel)),
      gst: Yup.string().required(validationMessages.required(GST_LABEL)),
      cost: Yup.number()
        .required(validationMessages.required(Cost))
        .positive()
        .integer(),
      gstAmount: Yup.string().required(
        validationMessages.required(QuotationLabel.GstAmount)
      ),
      withGstAmount: Yup.string().required(
        validationMessages.required(WithGstAmount)
      ),
    }),
    onSubmit: async (values) => {
      setSubmitLoader(true);
      const payload: ServicePayload = {
        service_name: values.service_name,
        cost: values.cost,
        serviceId: values.serviceId,
        hsn_sac_id: values.hsnSac,
        gst: values.gst,
        description: values.description,
        gst_amount: values.gstAmount,
        with_gst_amount: values.withGstAmount,
      };

      if (selectedServiceId) {
        updateService(payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              fetchServices();
              closeModal();
            } else {
              toast.error(res?.message);
            }
          })
          .catch((error) => {
            toast.error(error?.response?.data?.message || error?.message);
          })
          .finally(() => setSubmitLoader(false));
      } else {
        createService(payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              fetchServices();
              closeModal();
            } else {
              toast.error(res?.message);
            }
          })
          .catch((error) => {
            toast.error(error?.response?.data?.message || error?.message);
            setSubmitLoader(false);
          })
          .finally(() => setSubmitLoader(false));
      }
    },
  });

  const openDeleteModal = (id: number) => {
    setSelectedServiceId(id);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setSelectedServiceId(null);
  };

  const handleDeleteService = () => {
    if (selectedServiceId !== null) {
      setBtnLoader(true);
      deleteService(selectedServiceId)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            fetchServices();
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message || error?.message);
        })
        .finally(() => {
          setBtnLoader(false);
          closeDeleteModal();
        });
    }
  };

  const viewServiceData = (id: number) => {
    openModal();
    setModalLoader(true);
    viewService(id)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          formik.setValues({
            service_name: res?.data?.service_name ?? "",
            cost: res?.data?.cost ?? null,
            serviceId: res?.data?.id ?? null,
            hsnSac: res?.data?.hsnSac?.id ?? null,
            gst: res?.data?.gst ?? null,
            description: res?.data?.description ?? "",
            withGstAmount: res?.data?.with_gst_amount ?? null,
            gstAmount: res?.data?.gst_amount ?? null,
          });
          setSelectedServiceId(id);
        } else {
          toast.error(res?.message);
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || error?.message);
      })
      .finally(() => setModalLoader(false));
  };

  const openEditModal = async (id: number) => {
    setEditMode(true);
    viewServiceData(id);
    setModalLoader(true);
  };

  const handleSearchValueChange = (value: any) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };

  const handleFetchData = (page: any) => {
    setCurrentPage(page);
  };

  const handleFetchSorting = (
    page: any,
    id: SetStateAction<string>,
    order: SetStateAction<string>
  ) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  const serviceColumns = useMemo(
    () => [
      {
        header: serviceLabel,
        accessorKey: servicesKey,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: Description,
        accessorKey: descriptonKey,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: HSNSACLabel,
        accessorKey: hsnSacKey,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: Cost,
        accessorKey: costKey,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return rupeeSymbol(cell?.row?.original?.cost);
        },
      },
      {
        header: GST_LABEL,
        accessorKey: gstKey,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return TaxType.find((item)=> cell?.row?.original?.gst === item?.value)?.label;
        },
      },
      {
        header: GstAmount,
        accessorKey: gstAmountKey,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return rupeeSymbol(cell?.row?.original?.gst_amount);
        },
      },
      {
        header: WithGstAmount,
        accessorKey: amountKey,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return rupeeSymbol(cell?.row?.original?.with_gst_amount);
        },
      },
      {
        header: ActionLabel,
        accessorKey: ActionKey,
        cell: (cell: any) => (
          <div className="d-flex justify-content-start enable-action flex-wrap gap-2 icon">
            <span className="tooltip-container">
              <i
                className="mdi mdi-pencil-outline fs-4 text-info cursor-pointer"
                onClick={() => openEditModal(cell?.row?.original?.id)}
              ></i>
              <span className="tooltip-text">{editLabel}</span>
            </span>

            <span className="tooltip-container">
              <i
                className="mdi mdi-delete-outline fs-4 text-danger cursor-pointer"
                onClick={() => openDeleteModal(cell?.row?.original?.id)}
              ></i>
              <span className="tooltip-text">{DeleteLabel}</span>
            </span>
          </div>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    []
  );

  const calculateGst = () => {
    const payload = {
      rate: formik.values.cost,
      gst: formik.values.gst,
    }
    const isValidPayload= payload.gst !==null && payload.rate !== null;
    if(isValidPayload){
      calculateServiceGstApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            formik.setFieldValue("gstAmount", res?.data?.gstAmount);
            formik.setFieldValue("withGstAmount", res?.data?.totalGstAmount);
          }
        })
        .catch((err) => {
          setLoader(false);
        })
        .finally(() => {
          setLoader(false);
        });
    }
    
  }
  useEffect(() => {
    calculateGst();
  }, [formik.values.cost, formik.values.gst]);

  return (
    <div className="page-content">
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{servicesLabel}</span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton className="btn btn-success" onClick={openModal}>
              <i className="mdi mdi-plus" /> {NewLabel}
            </BaseButton>
          </div>
        </div>

        <div className="card py-2 px-4 overflow-x-auto">
          {loader || services.length > 0 ? (
            <TableContainer
              customPageSize={pageSize}
              setCustomPageSize={setPageSize}
              isGlobalFilter={true}
              columns={serviceColumns}
              data={searchValue ? serviceSearchData : services || []}
              tableClass="table bg-white"
              manualPagination={true}
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              onSearch={handleSearchValueChange}
              totalNumberOfRows={totalNumberOfRows}
              fetchData={handleFetchData}
              fetchSortingData={handleFetchSorting}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
        </div>

        <BaseModal
          open={isModalOpen}
          toggle={closeModal}
          title={editMode ? editServiceLabel : addServiceLabel}
          submit={formik.handleSubmit}
          closeLabel={cancelLabel}
          submitLabel={editMode ? updateLabel : submitLabel}
          closeLabelColor="danger"
          submitLabelColor="success"
          loader={submitLoader}
        >
          {modalLoader ? (
            <div className="d-flex justify-content-center align-items-center">
              <Spinner>{Loading}</Spinner>
            </div>
          ) : (
            <div className="form-group">
              <div className="row">
                <div className="col-12 col-md-6">
                  <BaseInput
                    type="text"
                    label={ServiceName}
                    placeholder={InputPlaceHolder(ServiceName)}
                    name="service_name"
                    className="form-control"
                    value={formik.values.service_name}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    touched={formik.touched.service_name}
                    error={formik.errors.service_name}
                    required
                  />
                </div>
                <div className="col-12 col-md-6">
                  <BaseInput
                    type="textarea"
                    label={Description}
                    placeholder={InputPlaceHolder(Description)}
                    name="description"
                    className="form-control"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    touched={formik.touched.description}
                    error={formik.errors.description}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-md-6">
                  <BaseSelect
                    name="hsnSac"
                    label={HSNSACLabel}
                    options={hsnSacOption}
                    placeholder={SelectPlaceHolder(HSNSACLabel)}
                    value={formik.values.hsnSac}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.hsnSac}
                    touched={formik.touched.hsnSac}
                    required
                  />
                </div>
                <div className="col-12 col-md-6">
                  <BaseInput
                    type="number"
                    label={Cost}
                    placeholder={InputPlaceHolder(Cost)}
                    name="cost"
                    className="form-control"
                    value={formik.values.cost}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    touched={formik.touched.cost}
                    error={formik.errors.cost}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-md-6">
                  <BaseSelect
                    name="gst"
                    label={GST_LABEL}
                    options={TaxType || []}
                    value={formik.values.gst}
                    placeholder={SelectPlaceHolder(GST_LABEL)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.gst}
                    touched={formik.touched.gst}
                    required
                  />
                </div>
                <div className="col-12 col-md-6">
                  <BaseInput
                    type="number"
                    label={GstAmount}
                    placeholder={InputPlaceHolder(GstAmount)}
                    name="gstAmount"
                    className="form-control"
                    value={formik.values.gstAmount}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    touched={formik.touched.gstAmount}
                    error={formik.errors.gstAmount}
                    required
                    disabled
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-md-6">
                  <BaseInput
                    type="number"
                    label={WithGstAmount}
                    placeholder={InputPlaceHolder(WithGstAmount)}
                    name="withGstAmount"
                    className="form-control"
                    value={formik.values.withGstAmount}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    touched={formik.touched.withGstAmount}
                    error={formik.errors.withGstAmount}
                    required
                    disabled
                  />
                </div>
              </div>
            </div>
          )}
        </BaseModal>

        <DeleteModal
          show={deleteModal}
          onCloseClick={closeDeleteModal}
          onDeleteClick={handleDeleteService}
          btnLoader={btnLoader}
        />
      </div>
    </div>
  );
};

export default Services;
